import { useRef, useState, useCallback } from "react";
import { addMinutes, differenceInMinutes } from "date-fns";
import { isModifierKeyPressed } from "@gettactic/helpers";
import {
  clampToRange,
  roundToSnap
} from "@gettactic/helpers/src/date-time/timelineUtils";

export interface UseTimelineDragSelectOptions {
  /** Ref to the container element for measuring positions */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** The date being displayed (used for clamping to day boundaries) */
  date: Date;
  /** Snap interval in minutes (default: 5) */
  snapMinutes?: number;
  /** Modifier snap interval in minutes (used when CMD/CTRL is held or mobile toggle is on) */
  modifierSnapMinutes?: number;
  /** Minimum duration in minutes (default: 15) */
  minDurationMinutes?: number;
  /** Pixels per minute scaling factor */
  pixelsPerMinute: number;
  /** Start hour of visible range (default: 0) */
  visibleStartHour?: number;
  /** End hour of visible range (default: 24) */
  visibleEndHour?: number;
  /** Callback when range changes (both start and end times) */
  onRangeChange?: (start: Date, end: Date) => void;
  /** Drag threshold in pixels for desktop/mouse (default: 5) */
  dragThresholdPx?: number;
  /** Drag threshold in pixels for mobile/touch (default: 10) */
  touchDragThresholdPx?: number;
  /** Current start time (used to detect if selection exists for click-to-reposition) */
  currentStartTime?: Date | null;
  /** Current end time (used to detect if selection exists for click-to-reposition) */
  currentEndTime?: Date | null;
  /** Vertical padding at top of container in pixels (default: 0) */
  containerPaddingTop?: number;
  /** Mobile snap toggle: when true, use modifier snap interval instead of default */
  mobileSnapEnabled?: boolean;
}

export interface UseTimelineDragSelectResult {
  /** Handler for pointer down on background */
  onBgPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  /** Handler for pointer move */
  onBgPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  /** Handler for pointer up/cancel */
  onBgPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  /** Pending start time during drag (for visual preview) */
  pendingStart: Date | null;
  /** Pending end time during drag (for visual preview) */
  pendingEnd: Date | null;
  /** Whether currently dragging */
  isDragging: boolean;
}

/**
 * Hook for implementing drag-to-select behavior on timeline components
 *
 * Supports:
 * - Drag on background: creates new time selection
 * - Click on background: repositions existing selection (preserves duration)
 * - Hold CMD (Mac) or CTRL (Windows/Linux): snap to modifier interval for fine control
 * - Backwards dragging (auto-swaps start/end)
 * - Configurable snap intervals
 * - Mobile/touch support with different drag thresholds
 * - Minimum duration enforcement
 */
export function useTimelineDragSelect(
  options: UseTimelineDragSelectOptions
): UseTimelineDragSelectResult {
  const {
    containerRef,
    date,
    snapMinutes = 5,
    modifierSnapMinutes = 15,
    minDurationMinutes = 15,
    pixelsPerMinute,
    visibleStartHour = 0,
    visibleEndHour = 24,
    onRangeChange,
    dragThresholdPx = 5,
    touchDragThresholdPx = 10,
    currentStartTime,
    currentEndTime,
    containerPaddingTop = 0,
    mobileSnapEnabled = false
  } = options;

  // Detect touch device
  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);
  const threshold = isTouchDevice ? touchDragThresholdPx : dragThresholdPx;

  const [isDragging, setIsDragging] = useState(false);
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [pendingEnd, setPendingEnd] = useState<Date | null>(null);

  const dragRef = useRef<{
    pointerId: number;
    startY: number;
    startDate: Date;
    modifierKeyPressed: boolean; // CMD/CTRL for modifier snap interval
  } | null>(null);

  /**
   * Convert Y pixel position to Date
   */
  const yToDate = useCallback(
    (y: number): Date => {
      // Subtract container padding to get position within content area
      const yInContent = y - containerPaddingTop;
      const minutesFromStart = yInContent / pixelsPerMinute;
      const totalMins = visibleStartHour * 60 + minutesFromStart;
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      d.setMinutes(totalMins);
      return d;
    },
    [date, visibleStartHour, pixelsPerMinute, containerPaddingTop]
  );

  /**
   * Handle pointer down on background
   */
  const onBgPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const container = containerRef.current;
      if (!container) return;

      // Ignore clicks on interactive elements
      const target = e.target as HTMLElement;
      if (target.closest('[data-selection-block="true"]')) return;
      if (target.closest('[data-resize-handle="true"]')) return;
      if (target.tagName === "BUTTON" || target.closest("button")) return;
      if (target.tagName === "SVG" || target.closest("svg")) return;

      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);

      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top + container.scrollTop;

      // Check if CMD (Mac) or CTRL (Windows/Linux) is pressed OR mobile toggle for modifier snap
      const modifierKeyPressed = isModifierKeyPressed(e);
      const effectiveSnap =
        modifierKeyPressed || mobileSnapEnabled
          ? modifierSnapMinutes
          : snapMinutes;

      const startDateRaw = yToDate(y);
      const start = clampToRange(
        roundToSnap(startDateRaw, effectiveSnap),
        date,
        visibleStartHour,
        visibleEndHour
      );

      // Store drag start position, but don't set pending values yet
      // We'll determine if it's a click or drag in onPointerMove/onPointerUp
      dragRef.current = {
        pointerId: e.pointerId,
        startY: y,
        startDate: start,
        modifierKeyPressed
      };

      setIsDragging(true);

      e.preventDefault();
    },
    [
      containerRef,
      yToDate,
      snapMinutes,
      date,
      visibleStartHour,
      visibleEndHour,
      minDurationMinutes
    ]
  );

  /**
   * Handle pointer move during drag
   */
  const onBgPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current || !isDragging) return;

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const yNow = e.clientY - rect.top + container.scrollTop;

      // Calculate drag distance
      const dragDistance = Math.abs(yNow - dragRef.current.startY);

      // If below threshold, don't update (treat as click, not drag)
      if (dragDistance < threshold) return;

      const { startDate } = dragRef.current;

      // Check current modifier key state OR mobile toggle for dynamic snap adjustment
      const modifierKeyPressed = isModifierKeyPressed(e);
      const effectiveSnap =
        modifierKeyPressed || mobileSnapEnabled
          ? modifierSnapMinutes
          : snapMinutes;

      // Drag-to-create: range from start to current position
      let s = startDate;
      let eDate = yToDate(yNow);

      // Support backwards dragging (swap start/end)
      if (eDate < s) [s, eDate] = [eDate, s];

      s = clampToRange(
        roundToSnap(s, effectiveSnap),
        date,
        visibleStartHour,
        visibleEndHour
      );
      eDate = clampToRange(
        roundToSnap(eDate, effectiveSnap),
        date,
        visibleStartHour,
        visibleEndHour
      );

      // Enforce minimum duration
      if (differenceInMinutes(eDate, s) < minDurationMinutes) {
        eDate = addMinutes(s, minDurationMinutes);
      }

      setPendingStart(s);
      setPendingEnd(eDate);

      e.preventDefault();
    },
    [
      containerRef,
      isDragging,
      yToDate,
      snapMinutes,
      date,
      visibleStartHour,
      visibleEndHour,
      minDurationMinutes,
      threshold
    ]
  );

  /**
   * Handle pointer up - commit the selection
   */
  const onBgPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const yNow = e.clientY - rect.top + container.scrollTop;
      const dragDistance = Math.abs(yNow - dragRef.current.startY);
      const wasClick = dragDistance < threshold;

      // Check modifier key state OR mobile toggle for snap adjustment
      const modifierKeyPressed = isModifierKeyPressed(e);
      const effectiveSnap =
        modifierKeyPressed || mobileSnapEnabled
          ? modifierSnapMinutes
          : snapMinutes;

      if (wasClick) {
        // Click behavior: reposition existing selection if it exists
        const hasExistingSelection = currentStartTime && currentEndTime;

        if (hasExistingSelection && onRangeChange) {
          // Calculate duration of existing selection
          const duration = differenceInMinutes(
            currentEndTime,
            currentStartTime
          );

          // Move start to current cursor position, preserve duration
          const clickDate = yToDate(yNow);
          const newStart = clampToRange(
            roundToSnap(clickDate, effectiveSnap),
            date,
            visibleStartHour,
            visibleEndHour
          );
          let newEnd = addMinutes(newStart, duration);

          // Clamp end time
          newEnd = clampToRange(newEnd, date, visibleStartHour, visibleEndHour);

          // Adjust start if end was clamped
          let finalStart = newStart;
          if (differenceInMinutes(newEnd, newStart) < duration) {
            finalStart = clampToRange(
              addMinutes(newEnd, -duration),
              date,
              visibleStartHour,
              visibleEndHour
            );
          }

          onRangeChange(finalStart, newEnd);
        }
        // If no existing selection, do nothing on click
      } else {
        // Drag behavior: commit pending values
        if (pendingStart && pendingEnd && onRangeChange) {
          const s = clampToRange(
            roundToSnap(pendingStart, effectiveSnap),
            date,
            visibleStartHour,
            visibleEndHour
          );
          const eDate = clampToRange(
            roundToSnap(pendingEnd, effectiveSnap),
            date,
            visibleStartHour,
            visibleEndHour
          );

          onRangeChange(s, eDate);
        }
      }

      // Reset state
      dragRef.current = null;
      setIsDragging(false);
      setPendingStart(null);
      setPendingEnd(null);
    },
    [
      containerRef,
      pendingStart,
      pendingEnd,
      onRangeChange,
      snapMinutes,
      date,
      visibleStartHour,
      visibleEndHour,
      threshold,
      currentStartTime,
      currentEndTime
    ]
  );

  return {
    onBgPointerDown,
    onBgPointerMove,
    onBgPointerUp,
    pendingStart,
    pendingEnd,
    isDragging
  };
}
